import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Custom element powering the from-scratch "related products" section.
 * Lazy-loads recommendations via Shopify's built-in recommendations route
 * (works with any section, not just Horizon's native product-recommendations
 * block), and wires up a lightweight ajax add-to-cart button styled to match
 * the legacy WP markup.
 */
class RelatedProducts extends HTMLElement {
  #observer;

  connectedCallback() {
    this.target = this.querySelector('[data-related-target]');

    // Already server-rendered (e.g. requested directly through the
    // recommendations endpoint while in the theme editor) — just wire up buttons.
    if (this.target && this.target.children.length > 0) {
      this.#bindAddToCartButtons();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        this.#loadRecommendations();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  async #loadRecommendations() {
    const url = this.dataset.url;
    if (!url) return;

    try {
      const response = await fetch(url);
      if (!response.ok) return;

      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const newTarget = doc.querySelector('[data-related-target]');

      if (!newTarget || newTarget.children.length === 0) {
        this.hidden = true;
        return;
      }

      this.target.innerHTML = newTarget.innerHTML;
      this.#bindAddToCartButtons();
    } catch (error) {
      console.error('[related-products] Failed to load recommendations:', error);
      this.hidden = true;
    }
  }

  #bindAddToCartButtons() {
    this.querySelectorAll('[data-related-add-to-cart]').forEach((button) => {
      if (button.dataset.bound === 'true') return;
      button.dataset.bound = 'true';
      button.addEventListener('click', this.#handleAddToCart);
    });
  }

  #handleAddToCart = async (event) => {
    const button = event.currentTarget;
    const variantId = button?.dataset.variantId;

    if (!variantId || button.disabled) return;

    button.disabled = true;
    button.classList.add('is-loading');

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'related-products',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      // Request the cart drawer/section HTML in the add response so the cart
      // re-renders in real time, mirroring the native product-form flow.
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);
      const uniqueSectionIds = Array.from(new Set(sectionIds));

      const cartAddUrl = (typeof Theme !== 'undefined' && Theme.routes?.cart_add_url) || '/cart/add.js';
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: uniqueSectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });

      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Add to cart failed');
      }

      // Fetch the full cart (via the AJAX /cart.js endpoint) for an accurate
      // total quantity so the cart bubble count is correct.
      const cartUrl = (typeof Theme !== 'undefined' && Theme.routes?.cart_url) || '/cart';
      const cartResponse = await fetch(`${cartUrl}.js`, {
        headers: { Accept: 'application/json' },
      });
      const cart = await cartResponse.json();

      // The /cart/add.js response IS the line item that was just added — pass
      // only that as detail.items so the confirmation popup always shows the
      // product being added (not the last item already in the cart).
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: [result],
          source: 'related-products',
          sourceId: this.id || 'related-products',
          itemCount: cart.item_count,
          sections: result.sections,
          didError: false,
        },
      });

      button.classList.remove('is-loading');
      button.classList.add('is-added');
      setTimeout(() => button.classList.remove('is-added'), 1500);
    } catch (error) {
      console.error('[related-products] Add to cart failed:', error);
      deferredEventPromise.reject(error);
      button.classList.remove('is-loading');
      button.classList.add('is-error');
      setTimeout(() => button.classList.remove('is-error'), 1500);
    } finally {
      button.disabled = false;
    }
  };
}

if (!customElements.get('related-products')) {
  customElements.define('related-products', RelatedProducts);
}
